import React from "react";
import { Product } from "../types";
import { AddToCart } from "./AddToCart";
import { AddToCompare } from "./AddToCompare";
import { AddToWishlist } from "./AddToWishlist";

interface ProductCardProps {
  product: Product;
  flags?: string[];
  isInCompareList?: boolean;
  compareFull?: boolean;
  wishlist?: boolean;
  couponDiscountAmount?: number;
  cartId: string;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  flags,
  isInCompareList = false,
  compareFull = false,
  wishlist = false,
  couponDiscountAmount = 0,
  cartId,
}) => {
  // Get the first image or use a placeholder
  const imageUrl =
    product.images && product.images.length > 0
      ? `https://images.svijet-medija.hr${product.images[0]}`
      : "/themes/custom/barrio_smcart/images/no-image-icon.png";

  const statusMap: Record<string, string> = {
    "0": "Trenutno nije raspoloživo.",
    "10": "Raspoloživo odmah!",
    "100": "Raspoloživo odmah!",
    "70": "Raspoloživo odmah!",
    "60": "Raspoloživo odmah!",
    "20": "Raspoloživo uz narudžbu.",
    "30": "Trenutno nije raspoloživo.",
    "40": "U dolasku!",
    "50": "Na upit.",
  };

  // Determine availability status text and style
  const getAvailabilityStatus = () => {
    const status = product.availabilityStatus || "";
    const text = statusMap[status] || "Check Availability";

    // Determine color based on status code
    let className = "text-yellow-600"; // Default orange

    if (status === "0" || status === "30") {
      className = "text-red-600"; // Red for unavailable
    } else if (
      status === "10" ||
      status === "100" ||
      status === "70" ||
      status === "60"
    ) {
      className = "text-green-600"; // Green for immediately available
    }

    return { text, className, status };
  };

  const availability = getAvailabilityStatus();
  let showStrikePrice = true;
  let showCashPrice = true;

  if (!product.isBestPrice && !product.hasActiveDiscount) {
    showStrikePrice = false;
    showCashPrice = false;
  }

  if (product.isBestPrice && product.mpc >= product.strikePrice) {
    showStrikePrice = false;
  }

  if (product.hasActiveDiscount && product.cashPrice <= product.strikePrice) {
    showStrikePrice = true;
    showCashPrice = true;
  }

  return (
    <div className="node node--type-product node--view-mode-teaser clearfix product product--teaser">
      {/* Badge images in upper left corner */}
      {Array.isArray(flags) && flags.length > 0 && (
        <div className="product__discount-flags">
          {flags.map((url, i) => (
            <div className="product__discount-flag" key={i}>
              <img src={url} className="h-10 w-auto" />
            </div>
          ))}
        </div>
      )}

      {/* Product image */}
      <div className="product--teaser__image">
        <a href={product.path} className="block">
          <img src={imageUrl} alt={product.title} />
        </a>
      </div>

      <div className="product--teaser__content">
        <div className="product--teaser__content-availability">
          <span
            className={`availability-status availability-status--${availability.status}`}
          ></span>
          <div className={`product-availability__full`}>
            {availability.text}
          </div>
        </div>
        <a href={product.path}>
          <div className="product--teaser__content-title">
            <h2>{product.title}</h2>
          </div>
        </a>

        <div className="flex w-100">
          <div className="price product--teaser__content-price">
            <div className="flex flex-col ">
              <div className="product--teaser__content-price--old">
                <div className="product--teaser__content-price--old-strike">
                  {showStrikePrice && <span>€{product.strikePrice}</span>}
                </div>

                {product.discountPercent > 0 && (
                  <span className="product--teaser__content-price--percentage">
                    {" "}
                    {product.discountPercent.toFixed(2)} %
                  </span>
                )}
              </div>

              {showCashPrice ? (
                <div className="product__pricing-discount">
                  €{product.cashPrice}
                </div>
              ) : (
                <div className="product--teaser__content-price">
                  €{product.mpc}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="product--teaser__content-price-coupon">
          {couponDiscountAmount > 0 && (
            <span>
              <b>Dodatnih {couponDiscountAmount}%</b> uz promo kod
            </span>
          )}
        </div>
        <div className="product--teaser__content-add-to-cart">
          <AddToCart
            productId={product.nid}
            cartId={cartId}
            className="text-sm py-1 px-3"
            buttonText="Dodaj u košaricu"
          />
        </div>
        <div className="product--teaser__content-actions">
          <div className="product-compare-button-container">
            <AddToCompare
              productId={product.nid}
              className={`text-sm py-1 px-3 ${
                isInCompareList ? "text-red-600" : ""
              }`}
              buttonText="Usporedi"
              removeButtonText="Ukloni"
              isInCompareList={isInCompareList}
              compareFull={compareFull}
            />
          </div>
          <div className="wishlist-button-container">
            <AddToWishlist
              productId={product.nid}
              buttonText="Favorit"
              removeButtonText="Obriši favorit"
              isInWishlist={wishlist}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
